<template>
  <button @click="exportData" class="btn btn-primary mr-1">
    <i class="fa fa-download"></i>
    {{ $t('export') }}
    <b-modal v-model="exporting" :title="$t('exportingData')" @close="close" no-close-on-esc no-close-on-backdrop
             no-enforce-focus>
      <div class="alert alert-warning">
        <span>{{ $t('exportNotice') }}</span>
      </div>
      <div class="text-center">
        <b-spinner></b-spinner>
      </div>
    </b-modal>
  </button>
</template>

<script>
import { saveAs } from 'file-saver'
import Excel from 'exceljs'

export default {
  props: ['data', 'label', 'group'],
  data() {
    return {
      exporting: false,
      cancelRequest: false,
      downloadedCount: 0
    }
  },
  methods: {
    exportData: async function() {
      this.downloadedCount = 0
      this.exporting = true
      let workbook = new Excel.Workbook()
      let sheet = workbook.addWorksheet('data')
      this.setupSheet(sheet)

      let sum = {}
      let avg = {}
      this.data.forEach(item => {
        for (let key in item) {
          item[key] = Math.abs(item[key])
          if (!sum[key]) {
            sum[key] = 0
          }
          sum[key] += Math.abs(item[key])
        }
      })
      for (let key in sum) {
        avg[key] = Math.round(sum[key] / this.data.length)
      }

      if (sum) {
        let row = sum
        row.label = this.$t('total')
        row.pendingDuration = this.formatTime(row.pendingDuration)
        row.pendingBillDuration = this.formatTime(row.pendingBillDuration)
        row.confirmedDuration = this.formatTime(row.confirmedDuration)
        row.confirmedBillDuration = this.formatTime(row.confirmedBillDuration)
        row.canceledDuration = this.formatTime(row.canceledDuration)
        row.canceledBillDuration = this.formatTime(row.canceledBillDuration)
        row.totalDuration = this.formatTime(row.totalDuration)
        row.totalBillDuration = this.formatTime(row.totalBillDuration)
        sheet.addRow(row)
      }
      if (avg) {
        let row = avg
        row.label = this.$t('average')
        row.pendingDuration = this.formatTime(row.pendingDuration)
        row.pendingBillDuration = this.formatTime(row.pendingBillDuration)
        row.confirmedDuration = this.formatTime(row.confirmedDuration)
        row.confirmedBillDuration = this.formatTime(row.confirmedBillDuration)
        row.canceledDuration = this.formatTime(row.canceledDuration)
        row.canceledBillDuration = this.formatTime(row.canceledBillDuration)
        row.totalDuration = this.formatTime(row.totalDuration)
        row.totalBillDuration = this.formatTime(row.totalBillDuration)
        sheet.addRow(row)
      }
      this.data.forEach(item => {
        let row = item
        row.label = this.label(item)
        row.pendingDuration = this.formatTime(row.pendingDuration)
        row.pendingBillDuration = this.formatTime(row.pendingBillDuration)
        row.confirmedDuration = this.formatTime(row.confirmedDuration)
        row.confirmedBillDuration = this.formatTime(row.confirmedBillDuration)
        row.canceledDuration = this.formatTime(row.canceledDuration)
        row.canceledBillDuration = this.formatTime(row.canceledBillDuration)
        row.totalDuration = this.formatTime(row.totalDuration)
        row.totalBillDuration = this.formatTime(row.totalBillDuration)
        sheet.addRow(row)
        this.downloadedCount += 1
      })

      workbook.xlsx.writeBuffer({ base64: true }).then(data => {
        this.exporting = false
        saveAs(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          'admin-call-history-report.xlsx')
      }).finally(() => {
        this.cancelRequest = false
        this.downloadedCount = 0
        this.$emit('reload')
      })
    },
    setupSheet: function(sheet) {
      let cols = [
        { key: 'label', width: 20 },
        { key: 'pending', width: 20 },
        { key: 'pendingDuration', width: 20 },
        { key: 'pendingBillDuration', width: 20 },
        { key: 'confirmed', width: 20 },
        { key: 'confirmedDuration', width: 20 },
        { key: 'confirmedBillDuration', width: 20 },
        { key: 'canceled', width: 20 },
        { key: 'canceledDuration', width: 20 },
        { key: 'canceledBillDuration', width: 20 },
        { key: 'total', width: 20 },
        { key: 'totalDuration', width: 20 },
        { key: 'totalBillDuration', width: 20 }
      ]

      let rowHeader = {
        label: '',
        pending: this.$t('submit'),
        confirmed: this.$t('confirmed'),
        canceled: this.$t('canceled'),
        total: this.$t('total')
      }

      let row = {
        label: '',
        pending: this.$t('call'),
        pendingDuration: this.$t('duration'),
        pendingBillDuration: this.$t('billDuration'),
        confirmed: this.$t('call'),
        confirmedDuration: this.$t('duration'),
        confirmedBillDuration: this.$t('billDuration'),
        canceled: this.$t('call'),
        canceledDuration: this.$t('duration'),
        canceledBillDuration: this.$t('billDuration'),
        total: this.$t('call'),
        totalDuration: this.$t('duration'),
        totalBillDuration: this.$t('billDuration')
      }

      sheet.columns = cols
      sheet.addRow(rowHeader)
      sheet.addRow(row)
      sheet.mergeCells('B1:D1')
      sheet.mergeCells('E1:G1')
      sheet.mergeCells('H1:J1')
      sheet.mergeCells('K1:M1')
      sheet.getRow(1).eachCell(function(cell) {
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
      })
      sheet.getRow(2).eachCell(function(cell) {
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
      })
    },
    close() {
      this.cancelRequest = true
    },
    formatTime(value) {
      value = Math.round(value)
      let sec = value % 60
      let min = parseInt(value / 60)
      if (sec < 10) {
        sec = `0${sec}`
      }
      if (min < 10) {
        min = `0${min}`
      }
      return `${min}:${sec}`
    }
  },
  i18n: {
    messages: {
      en: {
        submit: 'Pending',
        confirmed: 'Confirmed',
        canceled: 'Canceled',
        call: 'Call',
        duration: 'Total duration (s)',
        billDuration: 'Talk duration (s)'
      },
      vi: {
        submit: 'Chờ duyệt',
        confirmed: 'Đã duyệt',
        canceled: 'Từ chối',
        call: 'Cuộc gọi',
        duration: 'Thời lượng (s)',
        billDuration: 'Thời gian đàm thoại (s)'
      }
    }
  }
}
</script>